// components/LoadingScreen.js - Solution anti-glitch pour desktop
import React, { useState, useEffect } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, Platform } from 'react-native';

/**
 * Composant de loading uniforme pour toute l'application
 * @param {Object} props - Props du composant
 * @param {string} props.message - Message à afficher (optionnel)
 * @param {string} props.variant - 'fullscreen' | 'content' | 'inline'
 * @param {Object} props.style - Style personnalisé pour le container
 */
const LoadingScreen = ({ 
  message = 'Loading...', 
  variant = 'fullscreen',
  style = {} 
}) => {
  // 🔧 ANTI-GLITCH: Délai avant affichage sur web desktop
  const [shouldShow, setShouldShow] = useState(Platform.OS !== 'web');

  useEffect(() => {
    if (Platform.OS === 'web') {
      // Délai minimal pour éviter flash sur desktop
      const timer = setTimeout(() => setShouldShow(true), 50);
      return () => clearTimeout(timer);
    }
  }, []);

  // Ne pas rendre si pas prêt sur web
  if (!shouldShow) return null;

  const getContainerStyle = () => {
    switch (variant) {
      case 'fullscreen':
        return [styles.fullscreenContainer, style];
      case 'content':
        return [styles.contentContainer, style];
      case 'inline':
        return [styles.inlineContainer, style];
      default:
        return [styles.fullscreenContainer, style];
    }
  };

  const getTextStyle = () => {
    switch (variant) {
      case 'fullscreen':
        return styles.fullscreenText;
      case 'content':
        return styles.contentText;
      case 'inline':
        return styles.inlineText;
      default:
        return styles.fullscreenText;
    }
  };

  const getIndicatorColor = () => {
    switch (variant) {
      case 'fullscreen':
        return '#ffffff';
      case 'content':
      case 'inline':
        return '#0066cc';
      default:
        return '#ffffff';
    }
  };

  return (
    <View style={getContainerStyle()}>
      <ActivityIndicator 
        size={variant === 'inline' ? 'small' : 'large'}
        color={getIndicatorColor()} 
      />
      {message && (
        <Text style={getTextStyle()}>
          {message}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Fullscreen - simple et stable
  fullscreenContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0066cc',
    zIndex: 9999,
  },
  fullscreenText: {
    color: '#ffffff',
    fontSize: 18,
    marginTop: 16,
    fontFamily: 'Montserrat_600SemiBold',
    textAlign: 'center',
    maxWidth: 280,
  },

  // Content - centrage fixe
  contentContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    zIndex: 100,
  },
  contentText: {
    color: '#333',
    fontSize: 16,
    marginTop: 12,
    fontFamily: 'Montserrat_500Medium',
    textAlign: 'center',
    maxWidth: 240,
  },

  // Inline - dimensions fixes
  inlineContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    minHeight: 48,
  },
  inlineText: {
    color: '#0066cc',
    fontSize: 14,
    marginLeft: 8,
    fontFamily: 'Montserrat_500Medium',
  },
});

export default LoadingScreen;